// ============================================================
// TMR Portal Admin — the Empresa form and its translation to the API payload
// ============================================================
import { maskCEP, onlyDigits } from '../../lib/format';
import { displayPhone, toE164Phone } from '../../lib/phone';
import type { BankAccountDetailsInput, BankAccountType, ReceivingAccount } from '../finance/contract';
import type {
  CompanyProfileRequest,
  ContactEmailSource,
  CompanyRecord,
  CompanySegment,
  PartyReferenceInput,
  OnboardCompanyRequest,
  UpdateCompanyRequest,
} from './contract';
import { DEFAULT_LEGACY_PORTAL_PERMISSIONS } from './portalPermissions';

/**
 * The registration as the wizard and the edit screen hold it: flat, all strings, masks and all.
 *
 * Screens work with what the operator types; {@link toOnboardRequest} and {@link toUpdateRequest} are the
 * one place where that becomes the payload — so normalization (masks off, empty means absent) is written
 * once and can be tested without a browser.
 */
export interface CompanyForm {
  name: string;
  legalName: string;
  /** Masked while typing; normalized to digits on submit. Only the onboarding sends it. */
  cnpj: string;
  subdomain: string;
  description: string;
  acquisitionOrigin: string;
  /**
   * The chosen classification's **id**, as a string — a `<select>` value is a string, and keeping it one
   * here means the form has a single empty state (`''`) instead of two (`''` and `0`, which is a real id
   * shape the backend would reject). It becomes a number in the payload.
   */
  classification: string;
  /**
   * The chosen segment's key, or `''` for none — the `<select>` value as it is. It goes out as-is, so a key
   * the backend knows and this portal does not yet survives an edit of some other field.
   */
  segment: string;
  specialBilling: boolean;

  /**
   * The Gestor de Venda's user id, or empty when none is chosen. Picked from real internal users; the
   * name is kept alongside only to show who is selected without a second lookup.
   */
  salesManagerUserId: string;
  salesManagerName: string;

  /** The Gestor de Pós-venda, same pair of fields and just as optional. */
  postSalesManagerUserId: string;
  postSalesManagerName: string;

  zipCode: string;
  country: string;
  state: string;
  city: string;
  district: string;
  street: string;
  number: string;
  complement: string;

  /**
   * The two people on the registration are references to a Party in the directory (10 B8.4). Each block
   * keeps the picked person's id alongside the visible fields, and {@link toProfileRequest} sends the id
   * when there is one and the typed details when there is not.
   *
   * The id is dropped by {@link editPerson} as soon as the operator edits any field of its block — so the
   * payload always describes what is on screen. Without that, editing the e-mail under a picked person
   * would submit an id that ignores the edit, and the field would silently revert on the next read.
   */
  representativePartyId: string;
  representativeName: string;
  representativeCpf: string;
  representativeEmail: string;
  representativePhone: string;

  focalContactPartyId: string;
  focalContactName: string;
  focalContactPhone: string;
  focalContactEmail: string;

  /**
   * Which e-mail is the Empresa's contact e-mail (the one the legacy Checkout shows). The representative's
   * unless the operator chooses otherwise — the same default the backend applies when it is omitted.
   */
  contactEmailSource: ContactEmailSource;
  /**
   * The typed address for the `Other` source. Kept while another source is chosen, so switching back does
   * not lose it, but only sent with `Other`.
   */
  contactEmail: string;

  /** Legacy Portal V1 screens granted to the representative's automatic type-2 login. */
  portalPermissions: string[];

  /**
   * The receiving account, which is not part of the Empresa's registration at all — it belongs to the
   * Finance module (13). It rides along on the wizard because onboarding provisions the Empresa's
   * default account from it; the edit screen saves it to its own resource, never through `PUT /companies`.
   */
  bankHolderLegalName: string;
  bankName: string;
  bankCode: string;
  bankAccountType: '' | BankAccountType;
  bankBranch: string;
  bankAccountNumber: string;
  bankAccountDigit: string;

  /**
   * Rates the wizard still collects and the backend does not accept: they are a first-class concern of
   * their own plan (08), with a per-product, per-installment-range model. Kept on screen so the operator
   * records them somewhere, never sent — sending them would be inventing a contract.
   */
  monthlyInterest: string;
  rateStandard: string;
  rateShared: string;
  rateCoProducer: string;
  creditLimit: string;
}

/** An empty registration, as the wizard opens. */
export const INITIAL_COMPANY_FORM: CompanyForm = {
  name: '',
  legalName: '',
  cnpj: '',
  subdomain: '',
  description: '',
  acquisitionOrigin: '',
  classification: '',
  segment: '',
  specialBilling: false,
  salesManagerUserId: '',
  salesManagerName: '',
  postSalesManagerUserId: '',
  postSalesManagerName: '',
  zipCode: '',
  country: 'Brasil',
  state: '',
  city: '',
  district: '',
  street: '',
  number: '',
  complement: '',
  representativePartyId: '',
  representativeName: '',
  representativeCpf: '',
  representativeEmail: '',
  representativePhone: '',
  focalContactPartyId: '',
  focalContactName: '',
  focalContactPhone: '',
  focalContactEmail: '',
  contactEmailSource: 'Representative',
  contactEmail: '',
  portalPermissions: [...DEFAULT_LEGACY_PORTAL_PERMISSIONS],
  bankHolderLegalName: '',
  bankName: '',
  bankCode: '',
  bankAccountType: '',
  bankBranch: '',
  bankAccountNumber: '',
  bankAccountDigit: '',
  monthlyInterest: '',
  rateStandard: '',
  rateShared: '',
  rateCoProducer: '',
  creditLimit: '30',
};

/** Trimmed, or null when the operator left it empty — the backend reads absent and empty differently. */
function optional(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** Digits only, or null when there are none. */
function optionalDigits(value: string): string | null {
  const digits = onlyDigits(value);
  return digits === '' ? null : digits;
}

/** The registration shared by onboarding and editing. */
export function toProfileRequest(form: CompanyForm): CompanyProfileRequest {
  return {
    name: form.name.trim(),
    legalName: form.legalName.trim(),
    subdomain: form.subdomain.trim(),
    description: optional(form.description),
    acquisitionOrigin: optional(form.acquisitionOrigin),
    // The id, not the label (12 §5). `Number.parseInt` rather than `Number` so a value that is not an id
    // becomes null instead of `NaN`, which JSON.stringify would silently send as `null` anyway — but only
    // after the form had already accepted it.
    classification: toClassificationId(form.classification),
    // The key, not the label (32 §5): the backend owns the catalogue and refuses anything else.
    segment: optional(form.segment) as CompanySegment | null,
    specialBilling: form.specialBilling,
    // The pickers set real internal users' ids; the backend validates each against Identity. Empty means
    // the operator left it unset, which the backend accepts for both roles.
    salesManagerUserId: optional(form.salesManagerUserId),
    postSalesManagerUserId: optional(form.postSalesManagerUserId),
    representative: toPartyReference(
      form.representativePartyId,
      form.representativeName,
      form.representativeCpf,
      form.representativeEmail,
      form.representativePhone,
    ),
    address: {
      street: form.street.trim(),
      number: form.number.trim(),
      complement: optional(form.complement),
      district: form.district.trim(),
      city: form.city.trim(),
      state: form.state.trim().toUpperCase(),
      zipCode: onlyDigits(form.zipCode),
      country: form.country.trim() || 'Brasil',
    },
    // The focal contact carries no CPF on screen — the block never collected one.
    focalContact: toOptionalPartyReference(
      form.focalContactPartyId,
      form.focalContactName,
      '',
      form.focalContactEmail,
      form.focalContactPhone,
    ),
    contactEmailSource: form.contactEmailSource,
    // Only `Other` carries an address: for the other two the backend resolves it from the person, and a
    // stale typed one would only read as a contradiction.
    ...(form.contactEmailSource === 'Other' ? { contactEmail: form.contactEmail.trim() } : {}),
  };
}

/**
 * The classification the form holds, as the payload's id.
 *
 * Anything that is not a positive integer becomes null — including the empty option, which is the common
 * case. It is worth a function rather than a cast: `Number('')` is `0`, and `0` is a shape the backend
 * treats as a real id and refuses, so the plain conversion would turn "não informado" into an error.
 *
 * @param value The `<select>` value.
 */
function toClassificationId(value: string): number | null {
  const id = Number.parseInt(value, 10);
  return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * `POST /companies/onboard` — the registration, the CNPJ and the bank block.
 *
 * The bank block is only sent here. Onboarding is the one moment the Empresa and its default receiving
 * account are created together; afterwards the account is its own resource in the Finance module.
 */
export function toOnboardRequest(form: CompanyForm): OnboardCompanyRequest {
  return {
    ...toProfileRequest(form),
    cnpj: onlyDigits(form.cnpj),
    bankAccount: toBankDetails(form),
    portalPermissions: [...form.portalPermissions],
  };
}

/** `PUT /companies/{id}` — the registration alone; neither the CNPJ nor the bank block belongs here. */
export function toUpdateRequest(form: CompanyForm): UpdateCompanyRequest {
  return toProfileRequest(form);
}

/**
 * A person block: the picked Party's id, or the details the operator typed — never both (10 B8.4).
 *
 * An id means the operator chose somebody from the directory and has not edited the block since, so the
 * details on screen are that person's and re-sending them would only invite the backend to match them
 * again. No id means the block was typed, and the backend matches or files the person from what is here.
 */
function toPartyReference(
  partyId: string,
  name: string,
  cpf: string,
  email: string,
  phone: string,
): PartyReferenceInput {
  const picked = optional(partyId);
  if (picked) return { partyId: picked, name: null, cpf: null, email: null, phone: null };

  return {
    partyId: null,
    name: optional(name),
    cpf: optionalDigits(cpf),
    email: optional(email),
    phone: toE164Phone(phone),
  };
}

/**
 * The same, for a person the Empresa may not have — null when the block is empty.
 *
 * A block the operator never filled in has to arrive absent rather than as an object full of nulls: the
 * backend reads the second as a person it cannot file and refuses the whole registration.
 */
function toOptionalPartyReference(
  partyId: string,
  name: string,
  cpf: string,
  email: string,
  phone: string,
): PartyReferenceInput | null {
  const person = toPartyReference(partyId, name, cpf, email, phone);
  const empty =
    person.partyId === null &&
    person.name === null &&
    person.cpf === null &&
    person.email === null &&
    person.phone === null;

  return empty ? null : person;
}

/**
 * Sets a field of a person block, dropping the picked Party id with it.
 *
 * Editing any field means the operator is describing a person, not pointing at one: from here the payload
 * carries the details and the backend matches or files them. Keeping the id would submit a reference that
 * ignores the edit — the backend lets the id win, and never edits a person it matched — so the field would
 * appear to save and then revert on the next read.
 *
 * @param set The form's setter.
 * @param key The person field being edited.
 * @param value Its new value.
 * @param partyIdKey The block's party-id field, cleared alongside.
 */
export function editPerson<K extends keyof CompanyForm>(
  set: <F extends keyof CompanyForm>(key: F, value: CompanyForm[F]) => void,
  key: K,
  value: CompanyForm[K],
  partyIdKey: 'representativePartyId' | 'focalContactPartyId',
): void {
  set(key, value);
  set(partyIdKey, '');
}

/**
 * Whether the form holds anything the operator would lose by closing it.
 *
 * Field by field against where it started. The permission list is the one field that is not a scalar, and
 * it is compared by content: switching a screen off and on again leaves nothing to lose, but it rebuilds
 * the array, so a reference comparison would ask about a discard that costs nothing.
 *
 * @param initial The form as it opened.
 * @param form The form as it stands.
 */
export function companyFormChanged(initial: CompanyForm, form: CompanyForm): boolean {
  return (Object.keys(initial) as (keyof CompanyForm)[]).some((key) => {
    const before = initial[key];
    const after = form[key];
    if (Array.isArray(before) && Array.isArray(after)) {
      return before.length !== after.length || before.some((value) => !after.includes(value));
    }
    return before !== after;
  });
}

/**
 * Whether the edit replaces the Empresa's legal representative with a different person.
 *
 * Not a field comparison, because {@link editPerson} clears the picked id the moment anyone types in the
 * block — so fixing a phone number would read as a replacement. This mirrors what the backend actually
 * does when it files the person: it matches by normalized CPF, and by e-mail when there is no CPF. Same
 * identifiers, same Party, nothing replaced.
 *
 * It matters because a replacement is not a cadastre correction: it closes the contracts that named the
 * previous representative and draws up a new one, which is worth asking about before it happens.
 *
 * @param initial The form as it opened.
 * @param form The form as it stands.
 */
export function representativeChanged(initial: CompanyForm, form: CompanyForm): boolean {
  const picked = optional(form.representativePartyId);
  if (picked) return picked !== optional(initial.representativePartyId);

  const cpf = optionalDigits(form.representativeCpf);
  const previousCpf = optionalDigits(initial.representativeCpf);
  if (cpf && previousCpf) return cpf !== previousCpf;

  const email = optional(form.representativeEmail)?.toLowerCase() ?? null;
  const previousEmail = optional(initial.representativeEmail)?.toLowerCase() ?? null;
  return email !== previousEmail;
}

/**
 * The bank details, or null when the operator left the block empty.
 *
 * All-or-nothing, like the backend's value object: half an account is worse than none, because a
 * transfer would be attempted with it. The screen's own validation asks for the rest as soon as one
 * field is filled.
 *
 * Exported because both paths need it: onboarding folds it into the payload, and the edit screen sends
 * it to the receiving-account resource.
 */
export function toBankDetails(form: CompanyForm): BankAccountDetailsInput | null {
  if (form.bankAccountType === '' || form.bankName.trim() === '') return null;

  return {
    holderLegalName: form.bankHolderLegalName.trim(),
    bankName: form.bankName.trim(),
    bankCode: onlyDigits(form.bankCode),
    accountType: form.bankAccountType,
    branch: onlyDigits(form.bankBranch),
    number: onlyDigits(form.bankAccountNumber),
    digit: form.bankAccountDigit.trim().toUpperCase(),
  };
}

/**
 * Fills the form from a stored record, so the edit screen starts from what is actually saved.
 *
 * The account comes in separately because it is a separate resource: the Empresa's record no longer
 * carries bank details, the Finance module does (13). Omit it and the bank fields open empty, which is
 * the honest state for an Empresa that has no receiving account yet.
 *
 * @param record The Empresa as `GET /companies/{id}` returned it.
 * @param account The Empresa's default receiving account, when it has one.
 */
export function fromRecord(record: CompanyRecord, account?: ReceivingAccount | null): CompanyForm {
  return {
    ...INITIAL_COMPANY_FORM,
    name: record.name,
    legalName: record.legalName ?? '',
    // Empty rather than absent: the field is a masked text input, and an incomplete record opens it blank
    // for somebody to fill in (19 §5).
    cnpj: record.cnpj ?? '',
    subdomain: record.subdomain,
    description: record.description ?? '',
    acquisitionOrigin: record.acquisitionOrigin ?? '',
    // The id, not the label: it is what the `<select>` matches its options on, and what a save sends back.
    // A classification the catalogue dropped keeps its id here, so editing another field does not silently
    // clear it.
    classification: record.classification ? String(record.classification.id) : '',
    segment: record.segment ?? '',
    specialBilling: record.specialBilling,
    salesManagerUserId: record.salesManager?.id ?? '',
    salesManagerName: record.salesManager?.name ?? '',
    postSalesManagerUserId: record.postSalesManager?.id ?? '',
    postSalesManagerName: record.postSalesManager?.name ?? '',
    // The backend stores the CEP as 8 bare digits, but the form (and its validation) work in the masked
    // shape the field shows — `maskCEP` is idempotent, so this is safe whatever comes back. Without it an
    // untouched address fails "CEP inválido" on save, because the field only re-masks what the operator
    // retypes; `toProfileRequest` strips the mask again on the way out.
    zipCode: maskCEP(record.address.zipCode),
    country: record.address.country,
    state: record.address.state,
    city: record.address.city,
    district: record.address.district,
    street: record.address.street,
    number: record.address.number,
    complement: record.address.complement ?? '',
    // The people are references into the Parties directory (10 B8.4): the id is what the Empresa stores,
    // the rest is the directory's answer at read time. Every detail is nullable — a reference the
    // directory no longer resolves opens the block empty, which is what makes the operator fix it before
    // saving (the e-mail is required, and it is the one the legacy producer login is matched by).
    // The block opens empty when the record has nobody on file — a legacy producer with no representative
    // (19 §5). Same treatment as a reference the directory no longer resolves: the form demands one before
    // it will save, which is exactly how the gap gets closed.
    representativePartyId: record.representative?.id ?? '',
    representativeName: record.representative?.name ?? '',
    representativeCpf: record.representative?.cpf ?? '',
    representativeEmail: record.representative?.email ?? '',
    representativePhone: displayPhone(record.representative?.phone),
    focalContactPartyId: record.focalContact?.id ?? '',
    focalContactName: record.focalContact?.name ?? '',
    focalContactPhone: displayPhone(record.focalContact?.phone),
    focalContactEmail: record.focalContact?.email ?? '',
    contactEmailSource: record.contactEmailSource ?? 'Representative',
    contactEmail: record.contactEmail ?? '',
    bankHolderLegalName: account?.holderLegalName ?? '',
    bankName: account?.bankName ?? '',
    bankCode: account?.bankCode ?? '',
    bankAccountType: account?.accountType ?? '',
    bankBranch: account?.branch ?? '',
    bankAccountNumber: account?.number ?? '',
    bankAccountDigit: account?.digit ?? '',
  };
}
